import {
  ObjectComboMapping,
  useCreateObjectComboMappingMutation,
  useDeleteObjectComboMappingMutation,
  useUpdateObjectComboMappingMutation
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useApp} from "../providers/RegionProvider/RegionProvider"
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";
import {EntityMutationCallback} from "./useEntityMutation";

export type UseObjectComboMappingUpdateProps = {}

export type UseObjectComboMappingUpdateResult = {
  createObjectComboMapping: (mapping: Partial<ObjectComboMapping>, callback: EntityMutationCallback<ObjectComboMapping> | undefined) => void
  updateObjectComboMapping: (mapping: Partial<ObjectComboMapping>, callback: EntityMutationCallback<ObjectComboMapping> | undefined) => void
  deleteObjectComboMapping: (id: string, callback: EntityMutationCallback | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useObjectComboMappingMutation = (props: UseObjectComboMappingUpdateProps = {}): UseObjectComboMappingUpdateResult => {
  const {regionId} = useApp()
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createObjectComboMapping, {isLoading: _isCreating}] = useCreateObjectComboMappingMutation()
  const [_updateObjectComboMapping, {isLoading: _isUpdating}] = useUpdateObjectComboMappingMutation()
  const [_deleteObjectComboMapping, {isLoading: _isDeleting}] = useDeleteObjectComboMappingMutation()

  useEffect(() => {
    _setUpdating(_isUpdating)
  }, [_isUpdating, _setUpdating])

  useEffect(() => {
    _setCreating(_isCreating)
  }, [_isCreating, _setCreating])

  useEffect(() => {
    _setDeleting(_isDeleting)
  }, [_isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useObjectComboMappingUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.objectcombomapping", "Object Combo Mapping"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useObjectComboMappingUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useObjectComboMappingUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.objectcombomapping", "Object Combo Mapping"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useObjectComboMappingUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useObjectComboMappingUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.objectcombomapping", "Object Combo Mapping"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useObjectComboMappingUpdate_deleting")
    }
  }, [_deleting, t]);

  const handlePromiseResult = useCallback((promise: Promise<ResultTypeFrom<any>>, title: string, callback: EntityMutationCallback<ObjectComboMapping> | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: title,
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"}),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      )
  }, [t])

  const createObjectComboMapping = useCallback((mapping: Partial<ObjectComboMapping>, callback: EntityMutationCallback<ObjectComboMapping> | undefined) => {
    const promise = _createObjectComboMapping(mapping).unwrap()
    handlePromiseResult(
      promise,
      t("common.entity.objectcombomapping", "Object Combo Mapping") + " " + t("common.status.created", "Created"),
      callback
    )
  }, [_createObjectComboMapping, handlePromiseResult, t])

  const updateObjectComboMapping = useCallback((mapping: Partial<ObjectComboMapping>, callback: EntityMutationCallback<ObjectComboMapping> | undefined) => {
    const promise = _updateObjectComboMapping(mapping).unwrap()
    handlePromiseResult(
      promise,
      t("common.entity.objectcombomapping", "Object Combo Mapping") + " " + t("common.status.updated", "Updated"),
      callback
    )
  }, [_updateObjectComboMapping, handlePromiseResult, t])

  const handleDeleteConfirm = useCallback((id: string, callback: EntityMutationCallback | undefined) => {
    const promise = _deleteObjectComboMapping({id: id}).unwrap()
    handlePromiseResult(
      promise,
      t("common.entity.objectcombomapping", "Object Combo Mapping") + " " + t("common.status.deleted", "Deleted"),
      callback
    )
  }, [_deleteObjectComboMapping, handlePromiseResult, t])

  const deleteObjectComboMapping = useCallback((id: string, callback: EntityMutationCallback | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.objectcombomapping", "Object Combo Mapping"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.deletemapping.confirm", "Are you sure you want to delete this mapping?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(id, callback)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createObjectComboMapping: createObjectComboMapping,
    updateObjectComboMapping: updateObjectComboMapping,
    deleteObjectComboMapping: deleteObjectComboMapping,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
